import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { AcrScanStatus, AcrScanResult } from "../../types/acr";
import type { MediaLabelTone } from "../../components/media-label/MediaLabel";

// 型定義
export type MediaItemFileType = 'audio' | 'video' | 'image' | 'document' | 'other';

/**
 * メディア変換（HLS / m4a 生成）の進捗。
 * complete 以外はフロントが原本ファイルにフォールバックしている状態で、
 * 動画では原本 MP4 を丸ごと読み込むため再生が重くなる。
 */
export type MediaConversionStatus = 'pending' | 'processing' | 'complete' | 'failed';

// コミュニケーション補助タグ（送信側/受信側で各1つ・プロジェクト全員に共有）
export type MediaItemProducerTag = 'recommended' | 'premise_adjustment' | 'alternative';
export type MediaItemClientTag = 'ok' | 'considering' | 'close' | 'different';

/** タグの陣営（producer: 送信側 / client: 受信側） */
export type MediaTagSide = 'producer' | 'client';

export const PRODUCER_TAG_LABELS: Record<MediaItemProducerTag, string> = {
  recommended: 'オススメ',
  premise_adjustment: '調整前提',
  alternative: '別案',
};

/**
 * 送信側タグごとの `MediaLabel` カラートーン。
 * ラベル文言（`PRODUCER_TAG_LABELS`）と対で管理する
 * （ProducerTagOverlay / ShotEditModal / MediaItemTable で共用）。
 */
export const PRODUCER_TAG_TONES: Record<MediaItemProducerTag, MediaLabelTone> = {
  recommended: 'blue',
  alternative: 'gray',
  premise_adjustment: 'outline',
};

export const CLIENT_TAG_LABELS: Record<MediaItemClientTag, string> = {
  ok: 'OK',
  considering: '検討',
  close: '近いが..',
  different: '違う',
};

/**
 * 他ショット参照（グローバルアセット）の参照先情報。
 * 参照アイテムはファイル系フィールド（file_* / original_url / waveform 等）が
 * 参照先ショット内の同名最新テイクへ動的解決されて返る。
 * 通常アイテムは null。
 */
export interface MediaItemReferenceSource {
  shot_id: string;
  shot_name: string | null;
  media_item_id: string;
}

export interface MediaItem {
  id: string; // UUID
  variation_name: string; // バリエーション名（ユーザー編集可能な表示名）
  file_name: string; // 実ファイル名（S3キーから算出）
  file_key: string; // S3キー
  file_type: MediaItemFileType;
  original_url: string; // 元ファイルURL
  optimized_url: string | null; // 最適化済みURL
  waveform_url: string | null; // 波形データJSON URL
  has_waveform: boolean; // 波形データの有無
  frame_rate?: number | null; // 動画のフレームレート（fps。動画以外・未解析は null）
  duration?: number | null; // メディアの長さ（秒。未解析は null）
  conversion_status?: MediaConversionStatus | null; // 変換パイプラインの進捗（ジョブ未作成は null）
  is_hls?: boolean; // optimized_url が HLS プレイリストかどうか
  thumbnail: string | null; // サムネイル画像URL
  variation_order: number; // バリエーション順序
  updated_at: string; // 更新日時
  shot_id: string; // 所属するショットID
  producer_tag?: MediaItemProducerTag | null; // 送信側タグ
  client_tag?: MediaItemClientTag | null; // 受信側タグ
  acr_scan_status?: AcrScanStatus; // ACR楽曲認識ステータス
  acr_scan_result?: AcrScanResult | null; // ACR楽曲認識結果
  acr_scanned_at?: string | null; // ACR Cloud送信成功日時（processing遷移時にセット）
  /** 他ショット参照（参照アイテムのみ。通常は null / undefined） */
  reference_source?: MediaItemReferenceSource | null;
}

export interface MediaItemsData {
  media_items: MediaItem[];
  count: number; // 総数
  has_next: boolean; // 次のページがあるか
  next_offset: number | null; // 次のオフセット
  limit: number; // 取得件数の制限
  offset: number; // 現在のオフセット
  filter: string; // 使用したフィルター
}

export interface MediaItemsResponse {
  status: string;
  data: MediaItemsData;
}

export interface MediaItemsQueryParams {
  shot_id: string; // ショットID
}

// 単一メディアアイテムを返す共通レスポンス型
export interface SingleMediaItemResponse {
  status: string;
  data: { media_item: MediaItem };
}

// メディアアイテム作成用の型定義（file_key か reference のどちらか一方）
export interface CreateMediaItemParams {
  shot_id: string;
  file_key?: string;
  /**
   * 他ショット参照（グローバルアセット）の作成。同一プロジェクト内のみ。
   * ファイルをコピーせず、参照先ショット内の同名最新テイクへ動的解決される
   */
  reference?: { shot_id: string; media_item_id: string };
  variation_name?: string;
  /** true で納品通知メールの対象外として作成（「通知せず更新」用。PATCH silent=false で解放） */
  silent?: boolean;
}

// メディアアイテム更新用の型定義
export interface UpdateMediaItemParams {
  shot_id: string;
  media_item_id: string;
  variation_name?: string;
  /** 表示用ファイル名（BE では display_file_name として保存され、S3 キー由来の名前を上書き表示） */
  file_name?: string;
  variation_order?: number;
  producer_tag?: MediaItemProducerTag | null;
  client_tag?: MediaItemClientTag | null;
  /** false で silent 状態を解除し通知メール対象として解放（「更新して通知」用） */
  silent?: boolean;
}

// メディアアイテム削除用の型定義
export interface DeleteMediaItemParams {
  shot_id: string;
  media_item_id: string;
}

export interface DeleteMediaItemResponse {
  status: string;
  data: { media_item_id: string };
}

// PATCH クエリの共通定義
const updateMediaItemQuery = ({ shot_id, media_item_id, ...body }: UpdateMediaItemParams) => ({
  url: `/api/${shot_id}/media-items/${media_item_id}`,
  method: "PATCH" as const,
  body,
});

/** 1ページあたりの取得件数（バックエンド MediaItemPagination.max_limit と同値） */
const MEDIA_ITEMS_PAGE_LIMIT = 100;
/** 全ページ取得の安全上限（無限ループ防止。100件 × 50ページ = 5000件） */
const MEDIA_ITEMS_MAX_PAGES = 50;
/** 2ページ目以降をまとめて取りに行くときの同時リクエスト数 */
const MEDIA_ITEMS_FETCH_CONCURRENCY = 4;

// Define a service using a base URL and expected endpoints
export const mediaItemsApi = createApi({
  reducerPath: "mediaItemsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["MediaItems"],
  endpoints: (builder) => ({
    // バックエンドはページネーション（デフォルト20件）で返すため、
    // 全ページを取得・結合して返す。
    // ショットのメディアはサイドバー・編集リストとも全件表示が前提。
    // 1ページ目の count で総数が分かるので、残りは has_next を直列に辿らず
    // オフセットを算出して並列に取得する（テイクの多いショットの初期表示対策）。
    getMediaItems: builder.query<MediaItemsResponse, MediaItemsQueryParams>({
      queryFn: async ({ shot_id }, _api, _extraOptions, fetchWithBQ) => {
        const fetchPage = (offset: number) =>
          fetchWithBQ(
            `/api/${shot_id}/media-items?limit=${MEDIA_ITEMS_PAGE_LIMIT}&offset=${offset}`,
          );

        const firstResult = await fetchPage(0);
        if (firstResult.error) return { error: firstResult.error };

        const firstData = (firstResult.data as MediaItemsResponse).data;
        const allItems: MediaItem[] = [...(firstData.media_items || [])];

        const totalPages = Math.min(
          Math.ceil((firstData.count || allItems.length) / MEDIA_ITEMS_PAGE_LIMIT),
          MEDIA_ITEMS_MAX_PAGES,
        );
        const restOffsets = Array.from(
          { length: Math.max(0, totalPages - 1) },
          (_, index) => (index + 1) * MEDIA_ITEMS_PAGE_LIMIT,
        );

        for (
          let start = 0;
          start < restOffsets.length;
          start += MEDIA_ITEMS_FETCH_CONCURRENCY
        ) {
          const results = await Promise.all(
            restOffsets
              .slice(start, start + MEDIA_ITEMS_FETCH_CONCURRENCY)
              .map(fetchPage),
          );
          for (const result of results) {
            if (result.error) return { error: result.error };
            allItems.push(
              ...((result.data as MediaItemsResponse).data.media_items || []),
            );
          }
        }

        return {
          data: {
            status: "success",
            data: {
              ...firstData,
              media_items: allItems,
              count: allItems.length,
              has_next: false,
              next_offset: null,
              offset: 0,
            },
          },
        };
      },
      providesTags: ["MediaItems"],
      keepUnusedDataFor: 0,
    }),

    createMediaItem: builder.mutation<SingleMediaItemResponse, CreateMediaItemParams>({
      query: ({ shot_id, ...body }) => ({
        url: `/api/${shot_id}/media-items`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MediaItems"],
    }),

    updateMediaItem: builder.mutation<SingleMediaItemResponse, UpdateMediaItemParams>({
      query: updateMediaItemQuery,
      invalidatesTags: ["MediaItems"],
    }),

    // 楽観的更新と組み合わせて使う（キャッシュ無効化しない）
    updateMediaItemSilent: builder.mutation<SingleMediaItemResponse, UpdateMediaItemParams>({
      query: updateMediaItemQuery,
    }),

    // タグのワンクリック切替用（楽観的更新でちらつきを防ぐ）
    updateMediaItemTags: builder.mutation<SingleMediaItemResponse, UpdateMediaItemParams>({
      query: updateMediaItemQuery,
      async onQueryStarted(
        { shot_id, media_item_id, producer_tag, client_tag },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          mediaItemsApi.util.updateQueryData('getMediaItems', { shot_id }, (draft) => {
            const item = draft.data?.media_items?.find((m) => m.id === media_item_id);
            if (!item) return;
            if (producer_tag !== undefined) item.producer_tag = producer_tag;
            if (client_tag !== undefined) item.client_tag = client_tag;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteMediaItem: builder.mutation<DeleteMediaItemResponse, DeleteMediaItemParams>({
      query: ({ shot_id, media_item_id }) => ({
        url: `/api/${shot_id}/media-items/${media_item_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MediaItems"],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetMediaItemsQuery,
  useLazyGetMediaItemsQuery,
  useCreateMediaItemMutation,
  useUpdateMediaItemMutation,
  useUpdateMediaItemSilentMutation,
  useUpdateMediaItemTagsMutation,
  useDeleteMediaItemMutation,
} = mediaItemsApi;

